import React, {
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useState,
} from "react";
import { useTranslation } from "react-i18next";
import VelavuSwitchBlock from "../../elements/velavu-switch-block";
import { patchArray } from "../../helper/array-helper";
import { dirtyCheckArray, usePreference } from "../../helper/hook-helper";
import { useSignInContext } from "../sign-in-context/sign-in-context";
import ComponentSaveBar from "./component-save-bar";
import * as styles from "./settings-alerts.module.scss";
import { alertEventCategories, EventCategory } from "velavu-js-api";
import { mapEventCategoryDescription } from "../../helper/language-helper";

export default function SettingsAlerts(props: {
	isDirty: boolean;
	setDirty: (dirty: boolean) => void;
}) {
	const { t } = useTranslation();
	const userContext = useSignInContext();
	const updateVelavuUser = userContext.updateVelavuUser;

	// Recompute category labels when language changes
	const eventCategoryArray = useMemo<[EventCategory, string][]>(
		() =>
			alertEventCategories
				.map((category): [EventCategory, string] => [
					category,
					mapEventCategoryDescription(category, t),
				])
				.sort(
					(
						[, a]: [EventCategory, string],
						[, b]: [EventCategory, string],
					) => Number(a > b) - Number(a < b),
				),
		[t],
	);
	const eventCategoryArraySplit = Math.ceil(eventCategoryArray.length / 2);

	const [devicesSelection, setDevicesSelection, devicesSelectionDirty] =
		usePreference(
			userContext.preferences.alertsEventCategoryFilter,
			dirtyCheckArray,
		);

	const [isLoading, setLoading] = useState(false);
	const [lastSaveResult, setLastSaveResult] = useState<boolean | null>(null);

	const saveChanges = useCallback(() => {
		updateVelavuUser({
			settings: { alertsEventCategoryFilter: devicesSelection },
		})
			.then(() => {
				setLastSaveResult(true);
			})
			.catch((error) => {
				console.log(error);
				setLastSaveResult(false);
			})
			.finally(() => setLoading(false));
	}, [devicesSelection, updateVelavuUser]);

	const propsSetDirty = props.setDirty;
	useEffect(
		() => propsSetDirty(devicesSelectionDirty),
		[devicesSelectionDirty, propsSetDirty],
	);

	return (
		<div className={styles.container}>
			<h1 className={styles.title}>
				{t("settings.alerts_settings.title")}
			</h1>
			<span className={styles.info}>
				{t("settings.alerts_settings.info")}
			</span>

			<div className={styles.inputSection}>
				<div className={styles.inputColumn}>
					{eventCategoryArray
						.slice(0, eventCategoryArraySplit)
						.map(([category, label]) => {
							const isSelected =
								devicesSelection.includes(category);
							return (
								<VelavuSwitchBlock
									key={category}
									className={styles.switchBlock}
									toggled={isSelected}
									onChange={() =>
										patchArray(
											setDevicesSelection,
											category,
											!isSelected,
										)
									}
									label={label}
								/>
							);
						})}
				</div>
				<div className={styles.inputSpacer} />
				<div className={styles.inputColumn}>
					{eventCategoryArray
						.slice(eventCategoryArraySplit)
						.map(([category, label]) => {
							const isSelected =
								devicesSelection.includes(category);
							return (
								<VelavuSwitchBlock
									key={category}
									className={styles.switchBlock}
									toggled={isSelected}
									onChange={() =>
										patchArray(
											setDevicesSelection,
											category,
											!isSelected,
										)
									}
									label={label}
								/>
							);
						})}
				</div>
			</div>

			<ComponentSaveBar
				className={styles.saveBar}
				disabled={!props.isDirty || isLoading}
				onClick={saveChanges}
				labelOK={t("settings.alerts_settings.saved")}
				labelError={t("settings.alerts_settings.save_failed")}
				result={lastSaveResult}
			/>
		</div>
	);
}
